import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import config from '../config';
import Cookies from 'js-cookie';
import axios from 'axios';

/**
 * Helper function to extract user ID from token and storage - OPTIMIZED: synchronous only
 * Defined at module level for better performance and to avoid hoisting issues
 */
const getUserIdFromToken = (token) => {
    try {
        // 1. Check localStorage userData first (most common)
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        if (userData && userData.id) {
            return userData.id;
        }

        // 2. Check localStorage user (alternative key)
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user && user.id) {
            return user.id;
        }
        
        // 3. Check sessionStorage (some apps use this)
        const sessionUser = JSON.parse(sessionStorage.getItem('userData') || sessionStorage.getItem('user') || '{}');
        if (sessionUser && sessionUser.id) {
            return sessionUser.id;
        }

        // 4. Try to decode JWT token payload (fallback)
        if (token) {
            try {
                const base64Url = token.split('.')[1];
                if (base64Url) {
                    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                    }).join(''));
                    const decoded = JSON.parse(jsonPayload);
                    if (decoded.user_id) {
                        return decoded.user_id;
                    }
                }
            } catch (jwtError) {
                // JWT decode failed - not critical
            }
        }

        return null;
    } catch (error) {
        return null;
    }
};

/**
 * Advanced Message Search Hook
 * Provides comprehensive message and conversation search functionality
 * matching the React Native app's Indox.tsx capabilities
 */
export const useMessageSearch = (initialQuery = '') => {
    const [query, setQuery] = useState(initialQuery);
    const [conversations, setConversations] = useState([]);
    const [filteredConversations, setFilteredConversations] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('all');
    const [searchHistory, setSearchHistory] = useState([]);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [pageSize] = useState(20); // Match backend default
    
    // Debounced search query
    const [debouncedQuery, setDebouncedQuery] = useState(query);
    
    // Debounce search input for better performance - optimized for real-time feel
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(query);
        }, 150); // Balanced: responsive but not excessive
        
        return () => clearTimeout(timer);
    }, [query]);

    // Add debounce ref to prevent excessive API calls
    const loadDebounceRef = useRef(null);
    const lastLoadTimeRef = useRef(0);

    // Load all conversations from backend with smart caching and pagination
    const loadConversations = useCallback(async (force = false, page = 1, append = false, silent = false) => {
        // Prevent rapid successive calls (debounce within 2 seconds)
        const now = Date.now();
        if (!force && !append && now - lastLoadTimeRef.current < 2000) {
            return; // Skip if called too recently
        }
        
        // Clear any pending debounced call
        if (loadDebounceRef.current) {
            clearTimeout(loadDebounceRef.current);
        }
        
        lastLoadTimeRef.current = now;
        
        // OPTIMIZATION: Load cached data immediately for instant display
        if (!append && page === 1 && !force) {
            try {
                const cachedData = localStorage.getItem('inbox_conversations_cache');
                if (cachedData) {
                    const { data, timestamp } = JSON.parse(cachedData);
                    // Use cache if less than 2 minutes old
                    if (Date.now() - timestamp < 120000 && data.length > 0) {
                        setConversations(data);
                        setIsLoading(false);
                        // Still fetch fresh data in background
                    }
                }
            } catch (e) {
                // Silent fail on cache read
            }
        }
        
        // CRITICAL FIX: Don't show loading state for background/silent refreshes
        // This prevents the flashing when WebSocket triggers a refresh
        if (append) {
            setIsLoadingMore(true);
        } else if (!silent && conversations.length === 0) {
            // Only show loading on initial load when there's no data
            setIsLoading(true);
        }
        setError(null);
        
        try {
            const token = Cookies.get('authToken');
            if (!token) {
                throw new Error('Authentication token not found');
            }

            // OPTIMIZATION: Get user ID synchronously from cache - never call API
            const userId = getUserIdFromToken(token);
            if (!userId) {
                throw new Error('User ID not found. Please log in again.');
            }
            
            const response = await axios.get(
                `${config.backendUrl}/messaging/chatroom/${userId}/`,
                {
                    headers: {
                        'Authorization': `Token ${token}`,
                        'Content-Type': 'application/json'
                    },
                    params: {
                        page: page,
                        page_size: page === 1 ? 10 : 20 // Ultra-small first page for instant load
                    },
                    timeout: 60000 // 60 second timeout for slower connections
                }
            );

            if (response.data && Array.isArray(response.data.results)) {
                const enrichedConversations = response.data.results.map(conv => {
                    // Get current user ID for ownership determination
                    const currentUserId = getUserIdFromToken(Cookies.get('authToken'));
                    
                    return {
                        ...conv,
                        searchableText: `${conv.sender_first_name || ''} ${conv.sender_last_name || ''} ${conv.sender_username || ''} ${conv.product_name || ''}`.toLowerCase(),
                        // Infer ownership based on available data
                        is_product_owner: conv.owner_id ? (conv.owner_id === parseInt(currentUserId)) : false,
                        // Ensure we have message count data
                        product_message_count: conv.product_message_count || conv.unread_count || 0
                    };
                });
                
                // Update pagination state
                setCurrentPage(page);
                setTotalCount(response.data.count || 0);
                setHasMore(!!response.data.next);
                setTotalPages(Math.ceil((response.data.count || 0) / pageSize));
                
                // Append or replace conversations
                if (append) {
                    setConversations(prev => [...prev, ...enrichedConversations]);
                } else {
                    // CRITICAL FIX: Only update state if data actually changed
                    // This prevents unnecessary re-renders and flashing
                    setConversations(prev => {
                        // Quick check: if same length and same first/last IDs, likely same data
                        if (prev.length === enrichedConversations.length && prev.length > 0) {
                            const prevIds = prev.map(c => `${c.id}-${c.updated_at}-${c.product_message_count}`).join(',');
                            const newIds = enrichedConversations.map(c => `${c.id}-${c.updated_at}-${c.product_message_count}`).join(',');
                            if (prevIds === newIds) {
                                return prev; // No change, keep existing reference
                            }
                        }
                        return enrichedConversations;
                    });
                    
                    // OPTIMIZATION: Cache page 1 for instant loading next time
                    if (page === 1) {
                        try {
                            localStorage.setItem('inbox_conversations_cache', JSON.stringify({
                                data: enrichedConversations,
                                timestamp: Date.now()
                            }));
                        } catch (e) {
                            // Silent fail on cache write (quota exceeded, etc.)
                        }
                    }
                }
            } else {
                setConversations([]);
            }
        } catch (err) {
            let errorMessage = 'Failed to load conversations. ';
            if (err.code === 'ECONNABORTED') {
                errorMessage += 'Request timeout. Please check your connection.';
            } else if (err.response?.status === 401) {
                errorMessage += 'Authentication failed. Please log in again.';
            } else if (err.response?.status === 403) {
                errorMessage += 'Access denied.';
            } else if (err.response?.status >= 500) {
                errorMessage += 'Server error. Please try again later.';
            } else {
                errorMessage += err.message || 'Please try again.';
            }
            
            setError(errorMessage);
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, [pageSize]);

    // Filter conversations based on search query and active tab
    const filterConversations = useCallback(() => {
        let filtered = [...conversations];
        
        // OPTIMIZATION: Early exit if no conversations
        if (filtered.length === 0) {
            // Only update if actually different
            setFilteredConversations(prev => prev.length === 0 ? prev : []);
            return;
        }
        
        // Apply search filter with enhanced real-time matching
        if (debouncedQuery.trim()) {
            const searchQuery = debouncedQuery.toLowerCase().trim();
            
            // OPTIMIZATION: Limit search to prevent performance issues with large datasets
            const searchLimit = 100; // Only search first 100 for real-time filtering
            const conversationsToSearch = filtered.slice(0, searchLimit);
            
            filtered = conversationsToSearch.filter(conversation => {
                // Extract all searchable fields with fallbacks
                const senderFirstName = (conversation.sender_first_name || '').toLowerCase();
                const senderLastName = (conversation.sender_last_name || '').toLowerCase();
                const senderFullName = `${senderFirstName} ${senderLastName}`.trim();
                const senderUsername = (conversation.sender_username || '').toLowerCase();
                const productName = (conversation.product_name || '').toLowerCase();
                const searchableText = conversation.searchableText || '';
                
                // Real-time search strategies for comprehensive matching
                
                // 1. Direct substring matching (fastest)
                if (senderFirstName.includes(searchQuery) ||
                    senderLastName.includes(searchQuery) ||
                    senderFullName.includes(searchQuery) ||
                    senderUsername.includes(searchQuery) ||
                    productName.includes(searchQuery) ||
                    searchableText.includes(searchQuery)) {
                    return true;
                }
                
                // 2. Username-specific search with @ prefix
                if (searchQuery.startsWith('@') && senderUsername.includes(searchQuery.substring(1))) {
                    return true;
                }
                
                // 3. First letter matching for initials
                if (searchQuery.length === 1 && (
                    senderFirstName.startsWith(searchQuery) ||
                    senderLastName.startsWith(searchQuery) ||
                    productName.startsWith(searchQuery))) {
                    return true;
                }
                
                // 4. Word-based matching for partial searches
                const searchWords = searchQuery.split(/\s+/).filter(word => word.length > 0);
                if (searchWords.length > 1) {
                    const flexibleWordMatch = searchWords.some(word => 
                        senderFirstName.includes(word) ||
                        senderLastName.includes(word) ||
                        senderUsername.includes(word) ||
                        productName.includes(word) ||
                        senderFullName.includes(word)
                    );
                    if (flexibleWordMatch) return true;
                }
                
                return false;
            });
        }

        // Apply tab filter
        switch (activeTab) {
            case 'my-products':
                // Filter conversations where current user is the product owner
                filtered = filtered.filter(conv => {
                    const isOwner = conv.is_product_owner || 
                                  (conv.owner_id && conv.owner_id === parseInt(getUserIdFromToken(Cookies.get('authToken'))));
                    return isOwner;
                });
                break;
            case 'contacted-products':
                // Filter conversations where current user contacted someone else's product
                filtered = filtered.filter(conv => {
                    const isOwner = conv.is_product_owner || 
                                  (conv.owner_id && conv.owner_id === parseInt(getUserIdFromToken(Cookies.get('authToken'))));
                    return !isOwner;
                });
                break;
            case 'unread':
                // Filter unread conversations
                filtered = filtered.filter(conv => {
                    const unreadCount = conv.product_message_count || conv.unread_count || 0;
                    return unreadCount > 0;
                });
                break;
            case 'recent':
                // Sort by most recent activity
                filtered.sort((a, b) => new Date(b.last_update_date) - new Date(a.last_update_date));
                break;
            case 'all':
            default:
                // No additional filtering
                break;
        }
        
        // OPTIMIZATION: Only update state if filtered list actually changed
        setFilteredConversations(prev => {
            // Quick comparison by IDs and key properties
            if (prev.length === filtered.length) {
                const prevKey = prev.map(c => `${c.id}-${c.product_message_count}-${c.updated_at}`).join('|');
                const newKey = filtered.map(c => `${c.id}-${c.product_message_count}-${c.updated_at}`).join('|');
                if (prevKey === newKey) {
                    return prev; // No change, keep previous reference
                }
            }
            return filtered;
        });
    }, [debouncedQuery, conversations, activeTab]);

    // Load more conversations (pagination)
    const loadMoreConversations = useCallback(() => {
        if (!isLoadingMore && hasMore) {
            loadConversations(false, currentPage + 1, true);
        }
    }, [currentPage, hasMore, isLoadingMore, loadConversations]);
    
    // Refresh conversations (reload page 1) - silent mode to prevent flashing
    const refreshConversations = useCallback(() => {
        loadConversations(true, 1, false, true); // silent = true
    }, [loadConversations]);

    // Apply additional filters and sorting
    const applyFiltersAndSort = useCallback((conversations, filterBy, sortBy) => {
        let filtered = [...conversations];

        // Apply filter
        switch (filterBy) {
            case 'unread':
                filtered = filtered.filter(conv => (conv.product_message_count || 0) > 0);
                break;
            case 'read':
                filtered = filtered.filter(conv => (conv.product_message_count || 0) === 0);
                break;
            case 'all':
            default:
                // No additional filtering
                break;
        }

        // Apply sorting
        switch (sortBy) {
            case 'name':
                filtered.sort((a, b) => {
                    const nameA = `${a.sender_first_name || ''} ${a.sender_last_name || ''}`.toLowerCase();
                    const nameB = `${b.sender_first_name || ''} ${b.sender_last_name || ''}`.toLowerCase();
                    return nameA.localeCompare(nameB);
                });
                break;
            case 'unread':
                filtered.sort((a, b) => (b.product_message_count || 0) - (a.product_message_count || 0));
                break;
            case 'date':
            default:
                filtered.sort((a, b) => new Date(b.last_update_date) - new Date(a.last_update_date));
                break;
        }

        return filtered;
    }, []);

    // Generate enhanced search suggestions with real-time updates
    const generateSuggestions = useCallback((searchQuery) => {
        const query = searchQuery.toLowerCase().trim();
        const suggestionSet = new Set();
        
        if (query.length === 0) {
            setSuggestions([]);
            return;
        }
        
        // OPTIMIZATION: Limit suggestions to prevent performance issues
        const MAX_SUGGESTIONS = 8;
        
        // OPTIMIZATION: Only scan first 50 conversations for suggestions
        const conversationsToScan = conversations.slice(0, 50);
        
        // Add participant names with intelligent matching
        for (const conv of conversationsToScan) {
            if (suggestionSet.size >= MAX_SUGGESTIONS) break; // Early exit
            
            // Full name suggestions
            const firstName = conv.sender_first_name || '';
            const lastName = conv.sender_last_name || '';
            const fullName = `${firstName} ${lastName}`.trim();
            
            if (firstName.toLowerCase().includes(query)) {
                suggestionSet.add(firstName);
                if (suggestionSet.size >= MAX_SUGGESTIONS) break;
            }
            
            if (lastName.toLowerCase().includes(query)) {
                suggestionSet.add(lastName);
                if (suggestionSet.size >= MAX_SUGGESTIONS) break;
            }
            
            if (fullName && fullName.toLowerCase().includes(query)) {
                suggestionSet.add(fullName);
                if (suggestionSet.size >= MAX_SUGGESTIONS) break;
            }
            
            // Username suggestions
            if (conv.sender_username && conv.sender_username.toLowerCase().includes(query)) {
                suggestionSet.add(`@${conv.sender_username}`);
                if (suggestionSet.size >= MAX_SUGGESTIONS) break;
            }
            
            // Product name suggestions with priority
            if (conv.product_name && conv.product_name.toLowerCase().includes(query)) {
                suggestionSet.add(conv.product_name);
                if (suggestionSet.size >= MAX_SUGGESTIONS) break;
            }
        }
        
        // Prioritize and limit suggestions
        const suggestionsArray = Array.from(suggestionSet);
        
        // Sort suggestions by relevance (exact matches first, then partial)
        const sortedSuggestions = suggestionsArray.sort((a, b) => {
            const aLower = a.toLowerCase();
            const bLower = b.toLowerCase();
            
            // Exact start matches get priority
            const aStartsWithQuery = aLower.startsWith(query);
            const bStartsWithQuery = bLower.startsWith(query);
            
            if (aStartsWithQuery && !bStartsWithQuery) return -1;
            if (!aStartsWithQuery && bStartsWithQuery) return 1;
            
            // Then by length (shorter is more relevant)
            return a.length - b.length;
        });
        
        setSuggestions(sortedSuggestions.slice(0, MAX_SUGGESTIONS));
    }, [conversations]);

    // Search history management
    const loadSearchHistory = () => {
        try {
            const history = JSON.parse(localStorage.getItem('messageSearchHistory') || '[]');
            setSearchHistory(history.slice(0, 10)); // Keep last 10 searches
        } catch (err) {
            setSearchHistory([]);
        }
    };

    const addToSearchHistory = (searchTerm) => {
        if (!searchTerm.trim()) return;
        
        try {
            const newHistory = [searchTerm, ...searchHistory.filter(item => item !== searchTerm)].slice(0, 10);
            setSearchHistory(newHistory);
            localStorage.setItem('messageSearchHistory', JSON.stringify(newHistory));
        } catch (err) {
            // Silent fail
        }
    };

    const clearSearchHistory = () => {
        setSearchHistory([]);
        localStorage.removeItem('messageSearchHistory');
    };

    // Load conversations on mount
    useEffect(() => {
        loadConversations();
        loadSearchHistory();
    }, [loadConversations]);

    // Filter conversations when query or tab changes
    // OPTIMIZATION: Use ref to track previous values and avoid unnecessary updates
    const prevFilterInputsRef = useRef({ query: '', activeTab: 'all', conversationsLength: 0 });
    
    useEffect(() => {
        // Check if inputs actually changed to prevent unnecessary filtering
        const prev = prevFilterInputsRef.current;
        const inputsChanged = 
            prev.query !== debouncedQuery || 
            prev.activeTab !== activeTab ||
            prev.conversationsLength !== conversations.length;
        
        if (inputsChanged) {
            prevFilterInputsRef.current = { 
                query: debouncedQuery, 
                activeTab, 
                conversationsLength: conversations.length 
            };
            filterConversations();
        }
    }, [debouncedQuery, conversations, activeTab, filterConversations]);

    // Generate suggestions when query changes
    useEffect(() => {
        if (debouncedQuery.trim().length >= 2) {
            generateSuggestions(debouncedQuery);
        } else {
            setSuggestions([]);
        }
    }, [debouncedQuery, generateSuggestions]);

    // Fallback: Get user info from API if local extraction fails
    const getUserIdFromAPI = async (token) => {
        try {
            const response = await axios.get(
                `${config.backendUrl}/users/me/`,
                {
                    headers: {
                        'Authorization': `Token ${token}`,
                        'Content-Type': 'application/json'
                    },
                    timeout: 10000
                }
            );
            
            if (response.data && response.data.id) {
                // Cache the user data for future use
                localStorage.setItem('userData', JSON.stringify(response.data));
                return response.data.id;
            }
            return null;
        } catch (error) {
            return null;
        }
    };

    // Search within specific conversation messages
    const searchInConversation = async (conversationId, searchQuery) => {
        if (!searchQuery.trim()) return [];
        
        try {
            const token = Cookies.get('authToken');
            const response = await axios.get(
                `${config.backendUrl}/messaging/conversations/${conversationId}/search/`,
                {
                    params: { q: searchQuery },
                    headers: {
                        'Authorization': `Token ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            
            return response.data.results || [];
        } catch (err) {
            return [];
        }
    };

    // Advanced filtering methods
    const filterByDateRange = (startDate, endDate) => {
        const filtered = conversations.filter(conv => {
            const convDate = new Date(conv.last_update_date);
            return convDate >= startDate && convDate <= endDate;
        });
        setFilteredConversations(filtered);
    };

    const filterByParticipant = (participantName) => {
        const filtered = conversations.filter(conv => {
            const fullName = `${conv.sender_first_name || ''} ${conv.sender_last_name || ''}`.toLowerCase();
            return fullName.includes(participantName.toLowerCase());
        });
        setFilteredConversations(filtered);
    };

    const filterByProduct = (productName) => {
        const filtered = conversations.filter(conv => 
            conv.product_name && conv.product_name.toLowerCase().includes(productName.toLowerCase())
        );
        setFilteredConversations(filtered);
    };

    // Statistics and analytics - OPTIMIZED: memoize expensive calculations
    const getSearchStats = useMemo(() => {
        const stats = {
            totalConversations: totalCount, // Use backend total count
            filteredCount: filteredConversations.length,
            unreadCount: conversations.filter(conv => (conv.product_message_count || 0) > 0).length,
            myProductsCount: conversations.filter(conv => conv.is_product_owner).length,
            contactedProductsCount: conversations.filter(conv => !conv.is_product_owner).length,
            currentPage,
            totalPages,
            hasMore
        };
        return stats;
    }, [conversations, filteredConversations.length, totalCount, currentPage, totalPages, hasMore]);

    return {
        // Search state
        query,
        setQuery,
        debouncedQuery,
        suggestions,
        showSuggestions,
        setShowSuggestions,
        searchHistory,
        
        // Conversation data
        conversations: filteredConversations,
        allConversations: conversations,
        
        // Loading states
        isLoading,
        isLoadingMore,
        error,
        
        // Pagination
        currentPage,
        totalPages,
        totalCount,
        hasMore,
        loadMoreConversations,
        refreshConversations,
        
        // Tab management
        activeTab,
        setActiveTab,
        
        // Actions
        loadConversations,
        addToSearchHistory,
        clearSearchHistory,
        searchInConversation,
        applyFiltersAndSort,
        
        // Advanced filtering
        filterByDateRange,
        filterByParticipant,
        filterByProduct,
        
        // Statistics
        stats: getSearchStats
    };
};

export default useMessageSearch;
